import { useMemo, useState, type FormEvent } from 'react';
import { Plus, RefreshCcw } from 'lucide-react';
import { hasPermission, PERMISSIONS } from '@helios/authz';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  ErrorState,
  Input,
  toast,
} from '@helios/ui';
import { useAuth } from '@/features/auth/authContext';
import { LoadingRow, SettingsStatus, selectClassName } from '@/features/settings/settingsPanels';
import {
  SettingsPageFrame,
  SettingsField as Field,
  SettingsSection,
  SettingsTableScroll,
  settingsFilterRowClass,
} from '@/features/settings/settingsChrome';
import {
  useAdminPermissions,
  useAdminRoleMutations,
  useAdminRoles,
  useAdminTenants,
} from '@/hooks/useAdmin';
import type { AdminRoleItem, CreateAdminRoleInput } from '@/services/adminClient';
import { getSessionOriginKey } from '@/services/sessionOrigin';
import { useSessionScopeFence } from '@/hooks/useSessionScope';

export function SettingsRolesPage() {
  const auth = useAuth();
  const scope = JSON.stringify([
    auth.user?.id,
    auth.tenant?.id,
    [...new Set(auth.permissions)].sort(),
    auth.sessionId,
    auth.authVersion,
    auth.accessMode,
    getSessionOriginKey(),
  ]);
  return <RolesWorkspace key={scope} />;
}

function RolesWorkspace() {
  const auth = useAuth();
  const scope = useSessionScopeFence(PERMISSIONS.ROLES_MANAGE);
  const platform = hasPermission(auth.permissions, PERMISSIONS.PLATFORM_ADMIN);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [tenant, setTenant] = useState<{ id: string; name: string; status?: string }>({
    id: '',
    name: 'All tenants and system roles',
  });
  const [tenantPage, setTenantPage] = useState(1);
  const [tenantSearch, setTenantSearch] = useState('');
  const [tenantQ, setTenantQ] = useState('');
  const [creating, setCreating] = useState<{ id: string; name: string } | null>(null);
  const [editing, setEditing] = useState<AdminRoleItem | null>(null);
  const roles = useAdminRoles({
    page,
    pageSize: 25,
    q: q || undefined,
    tenantId: tenant.id || undefined,
  });
  const permissions = useAdminPermissions();
  const tenants = useAdminTenants(
    { page: tenantPage, pageSize: 25, q: tenantQ || undefined },
    platform,
  );
  const mutations = useAdminRoleMutations();
  const items = roles.data?.items ?? [];
  const meta = roles.data?.meta;
  const tenantItems = tenants.data?.items ?? [];
  const catalog = (permissions.data ?? []).filter(
    (item) => platform || auth.permissions.includes(item.key),
  );
  // The API says per role whether this caller may edit it (a tenant System
  // Admin holds platform:admin but cannot edit global roles); the local rule
  // is only a fallback for an older API.
  const canEdit = (role: AdminRoleItem) =>
    role.canEdit ??
    (platform ||
    (!role.isSystem &&
      !role.permissionKeys.includes(PERMISSIONS.PLATFORM_ADMIN) &&
      role.tenantId === auth.tenant?.id &&
      role.permissionKeys.every((key) => auth.permissions.includes(key))));

  return (
    <SettingsPageFrame
      title="Roles & permissions"
      description="Paged role grants. System roles and roles granting platform administration require a platform admin to edit."
      actions={
        <div className="flex flex-wrap gap-1.5">
          <Button type="button" variant="outline" size="xs" onClick={() => void roles.refetch()}>
            <RefreshCcw className="h-3.5 w-3.5" aria-hidden="true" />
            Refresh
          </Button>
          <Button
            type="button"
            size="xs"
            disabled={
              permissions.isLoading ||
              permissions.isError ||
              (tenant.id !== '' && tenant.status !== 'ACTIVE') ||
              !auth.tenant?.id
            }
            onClick={() =>
              setCreating(tenant.id ? tenant : { id: auth.tenant!.id, name: auth.tenant!.name })
            }
          >
            <Plus className="h-3.5 w-3.5" aria-hidden="true" />
            New role
          </Button>
        </div>
      }
    >
      <SettingsStatus
        message={null}
        error={
          roles.error instanceof Error
            ? roles.error
            : permissions.error instanceof Error
              ? permissions.error
              : null
        }
      />
      {platform ? (
        <SettingsSection
          title="Tenant scope"
          description="Search and page workspaces. New roles belong to the selected tenant, or your own tenant when viewing all."
        >
          <form
            className={`${settingsFilterRowClass} sm:grid-cols-[1fr_auto]`}
            onSubmit={(event) => {
              event.preventDefault();
              setTenantQ(tenantSearch.trim());
              setTenantPage(1);
            }}
          >
            <Field label="Find tenant">
              <Input
                value={tenantSearch}
                onChange={(event) => setTenantSearch(event.target.value)}
                placeholder="Name or slug"
              />
            </Field>
            <Button type="submit" size="xs" variant="outline">
              Find tenants
            </Button>
          </form>
          <Field label="Tenant filter">
            <select
              className={selectClassName}
              value={tenant.id}
              onChange={(event) => {
                const selected = tenantItems.find((item) => item.id === event.target.value);
                setTenant(
                  selected
                    ? { id: selected.id, name: selected.name, status: selected.status }
                    : { id: '', name: 'All tenants and system roles' },
                );
                setPage(1);
              }}
            >
              <option value="">All tenants and system roles</option>
              {tenant.id && !tenantItems.some((item) => item.id === tenant.id) ? (
                <option value={tenant.id}>{tenant.name}</option>
              ) : null}
              {tenantItems.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          </Field>
          {tenants.isError ? (
            <p role="alert" className="text-critical text-xs">
              Tenant directory unavailable. Refresh or search again.
            </p>
          ) : null}
          <div className="text-muted-foreground mt-2 flex flex-wrap items-center gap-2 text-xs">
            <span>
              Tenant page {tenantPage} of {Math.max(1, tenants.data?.meta.totalPages ?? 1)}
            </span>
            <Button
              size="xs"
              variant="outline"
              disabled={tenantPage <= 1}
              onClick={() => setTenantPage((value) => value - 1)}
            >
              Previous tenants
            </Button>
            <Button
              size="xs"
              variant="outline"
              disabled={!tenants.data?.meta || tenantPage >= tenants.data.meta.totalPages}
              onClick={() => setTenantPage((value) => value + 1)}
            >
              Next tenants
            </Button>
          </div>
        </SettingsSection>
      ) : null}
      <SettingsSection
        title="Roles"
        description="Each role is a named set of permission keys enforced on the API."
      >
        <form
          className={`${settingsFilterRowClass} mb-2 sm:grid-cols-[1fr_auto]`}
          onSubmit={(event) => {
            event.preventDefault();
            setQ(search.trim());
            setPage(1);
          }}
        >
          <Field label="Search roles">
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Role name or description"
            />
          </Field>
          <Button type="submit" size="xs" variant="outline">
            Apply role search
          </Button>
        </form>
        {roles.isLoading ? (
          <LoadingRow label="Loading roles…" />
        ) : roles.isError ? (
          <ErrorState
            title="Roles unavailable"
            description="The admin roles API did not respond."
          />
        ) : items.length === 0 ? (
          <EmptyState
            title="No roles"
            description="No roles matched this filter. Create a tenant role to grant a custom permission set."
          />
        ) : (
          <SettingsTableScroll label="Roles directory">
            <table className="w-full min-w-[720px] border-collapse text-left text-xs">
              <thead className="bg-card text-2xs text-muted-foreground uppercase tracking-wide">
                <tr>
                  {['Role', 'Scope', 'Users', 'Permissions', 'Actions'].map((label) => (
                    <th key={label} className="border-border border-b px-3 py-2 font-medium">
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((role) => (
                  <tr key={role.id} className="border-border/70 border-b last:border-b-0">
                    <td className="px-2 py-1.5">
                      <p className="text-foreground font-medium">{role.name}</p>
                      <p className="text-muted-foreground text-xs">
                        {role.description ?? 'No description'}
                      </p>
                    </td>
                    <td className="px-2 py-1.5">
                      <Badge variant={role.isSystem ? 'warning' : 'outline'}>
                        {role.isSystem ? 'System' : 'Tenant'}
                      </Badge>
                    </td>
                    <td className="text-muted-foreground px-2 py-1.5">{role.userCount}</td>
                    <td className="text-muted-foreground px-2 py-1.5">
                      {role.permissionKeys.length}
                    </td>
                    <td className="px-2 py-1.5">
                      <Button
                        type="button"
                        size="xs"
                        variant="outline"
                        disabled={!canEdit(role) || permissions.isLoading || permissions.isError}
                        aria-label={`Permissions for ${role.name}`}
                        onClick={() =>
                          setEditing({ ...role, permissionKeys: [...role.permissionKeys] })
                        }
                      >
                        Permissions
                      </Button>
                      {!canEdit(role) ? (
                        <p className="text-2xs text-muted-foreground mt-1">
                          {role.isSystem || role.permissionKeys.includes(PERMISSIONS.PLATFORM_ADMIN)
                            ? 'Requires platform admin access.'
                            : 'Requires grants covering this role.'}
                        </p>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </SettingsTableScroll>
        )}
        <div className="text-muted-foreground mt-2 flex flex-wrap items-center justify-between gap-2 text-xs">
          <span>
            {meta ? `${meta.total} roles · ` : ''}Page {page} of{' '}
            {Math.max(1, meta?.totalPages ?? 1)}
          </span>
          <div className="flex gap-2">
            <Button
              size="xs"
              variant="outline"
              disabled={page <= 1}
              onClick={() => setPage((value) => value - 1)}
            >
              Previous roles
            </Button>
            <Button
              size="xs"
              variant="outline"
              disabled={!meta || page >= meta.totalPages}
              onClick={() => setPage((value) => value + 1)}
            >
              Next roles
            </Button>
          </div>
        </div>
      </SettingsSection>
      {creating ? (
        <RoleCreateDialog
          targetName={creating.name}
          permissionKeys={catalog.map((item) => item.key)}
          submitting={mutations.create.isPending}
          onClose={() => setCreating(null)}
          onSubmit={async (input) => {
            await mutations.create.mutateAsync({
              ...input,
              ...(platform ? { tenantId: creating.id } : {}),
            });
            setCreating(null);
            toast.success('Role created');
          }}
        />
      ) : null}
      {editing ? (
        <RolePermissionsDialog
          key={`${editing.id}:${editing.version}`}
          role={editing}
          keys={[...new Set([...catalog.map((item) => item.key), ...editing.permissionKeys])]}
          submitting={mutations.updatePermissions.isPending}
          onClose={() => setEditing(null)}
          onReload={async () => {
            const result = await roles.refetch();
            if (result.isError) throw result.error ?? new Error('Reload failed');
            const fresh = result.data?.items.find((item) => item.id === editing.id);
            if (!fresh)
              throw new Error(
                'Role is outside the current page or filter. Close and reopen it from the current directory.',
              );
            if (!canEdit(fresh))
              throw new Error('Your current grants no longer allow editing this role.');
            setEditing({ ...fresh, permissionKeys: [...fresh.permissionKeys] });
            return fresh;
          }}
          onSubmit={async (permissionKeys) => {
            const origin = scope.identity;
            await mutations.updatePermissions.mutateAsync({
              id: editing.id,
              permissionKeys,
              expectedVersion: editing.version,
            });
            if (!scope.isCurrent(origin)) return;
            setEditing(null);
            toast.success('Permissions saved');
            // Any edited role may belong to the operator. Refresh authoritative
            // grants after the committed receipt; a refresh failure is not a save failure.
            try {
              const session = await auth.reloadSession();
              if (
                !session.authenticated &&
                (scope.isCurrent(origin) || getSessionOriginKey() === null)
              ) {
                toast.error('Permissions saved. Sign in again to refresh your access.');
              }
            } catch {
              if (scope.isCurrent(origin)) {
                toast.error(
                  'Permissions saved. Refreshing your access failed; sign in again to reload it.',
                );
              }
            }
          }}
        />
      ) : null}
    </SettingsPageFrame>
  );
}

function RoleCreateDialog({
  targetName,
  permissionKeys,
  submitting,
  onClose,
  onSubmit,
}: {
  targetName: string;
  permissionKeys: string[];
  submitting: boolean;
  onClose: () => void;
  onSubmit: (input: CreateAdminRoleInput) => Promise<void>;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitting) return;
    setError(null);
    try {
      await onSubmit({
        name: name.trim(),
        description: description.trim() || undefined,
        permissionKeys: selected,
      });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Create failed');
    }
  };
  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next && !submitting) onClose();
      }}
    >
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>New tenant role</DialogTitle>
          <DialogDescription>Creates a custom role in {targetName}.</DialogDescription>
        </DialogHeader>
        <form className="grid gap-3" onSubmit={submit}>
          <Field label="Name">
            <Input
              required
              minLength={2}
              maxLength={80}
              disabled={submitting}
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </Field>
          <Field label="Description">
            <Input
              maxLength={240}
              disabled={submitting}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
            />
          </Field>
          <PermissionChecklist
            keys={permissionKeys}
            selected={selected}
            onChange={setSelected}
            disabled={submitting}
          />
          {error ? (
            <p role="alert" className="text-critical text-xs">
              {error}
            </p>
          ) : null}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={submitting}
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button type="submit" size="sm" disabled={submitting || name.trim().length < 2}>
              Create role
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function RolePermissionsDialog({
  role,
  keys,
  submitting,
  onClose,
  onSubmit,
  onReload,
}: {
  role: AdminRoleItem;
  keys: string[];
  submitting: boolean;
  onClose: () => void;
  onSubmit: (keys: string[]) => Promise<void>;
  onReload: () => Promise<AdminRoleItem>;
}) {
  const [selected, setSelected] = useState<string[]>(role.permissionKeys);
  const [error, setError] = useState<string | null>(null);
  const [reloading, setReloading] = useState(false);
  const dirty = [...selected].sort().join('\n') !== [...role.permissionKeys].sort().join('\n');
  const busy = submitting || reloading;
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (busy) return;
    setError(null);
    try {
      await onSubmit(selected);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Save failed');
    }
  };
  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next && !busy) onClose();
      }}
    >
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{role.name}</DialogTitle>
          <DialogDescription>
            Editing role version {role.version}. Reload current grants replaces your draft; Revert
            restores the grants shown when opened.
          </DialogDescription>
        </DialogHeader>
        <form className="grid gap-3" onSubmit={submit}>
          <PermissionChecklist
            keys={keys}
            selected={selected}
            onChange={setSelected}
            disabled={busy}
          />
          {error ? (
            <p role="alert" className="text-critical text-xs">
              {error}
            </p>
          ) : null}
          <DialogFooter className="flex-wrap">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={busy}
              onClick={async () => {
                setReloading(true);
                setError(null);
                try {
                  const fresh = await onReload();
                  setSelected([...fresh.permissionKeys]);
                } catch (caught) {
                  setError(caught instanceof Error ? caught.message : 'Reload failed');
                } finally {
                  setReloading(false);
                }
              }}
            >
              Reload current grants
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={busy || !dirty}
              onClick={() => {
                setSelected([...role.permissionKeys]);
                setError(null);
              }}
            >
              Revert
            </Button>
            <Button type="button" variant="outline" size="sm" disabled={busy} onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" size="sm" disabled={busy || !dirty}>
              Save permissions
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function PermissionChecklist({
  keys,
  selected,
  onChange,
  disabled,
}: {
  keys: string[];
  selected: string[];
  onChange: (keys: string[]) => void;
  disabled: boolean;
}) {
  const chosen = useMemo(() => new Set(selected), [selected]);
  const grouped = useMemo(() => {
    const groups = new Map<string, string[]>();
    for (const key of keys) {
      const domain = key.split(':')[0] ?? 'other';
      groups.set(domain, [...(groups.get(domain) ?? []), key]);
    }
    return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [keys]);
  return (
    <fieldset className="grid gap-2" disabled={disabled}>
      <legend className="text-muted-foreground text-xs font-medium uppercase tracking-wide">
        Permissions
      </legend>
      <div className="border-border max-h-64 space-y-2 overflow-y-auto rounded-md border p-2">
        {grouped.map(([domain, domainKeys]) => (
          <section key={domain}>
            <h3 className="text-2xs text-muted-foreground mb-1 font-semibold uppercase tracking-wider">
              {domain}
            </h3>
            <div className="grid gap-1">
              {domainKeys.map((key) => (
                <label key={key} className="flex items-center gap-2 text-xs">
                  <input
                    type="checkbox"
                    className="accent-primary h-4 w-4"
                    checked={chosen.has(key)}
                    onChange={() =>
                      onChange(
                        chosen.has(key)
                          ? selected.filter((item) => item !== key)
                          : [...selected, key],
                      )
                    }
                  />
                  <span className="font-mono">{key}</span>
                </label>
              ))}
            </div>
          </section>
        ))}
      </div>
    </fieldset>
  );
}
